/**
 * @module index
 * @package @geenius/adapters/solidjs
 * @description Defines the SolidJS adapter primitives for reading adapter
 * instances and adapter status state from the SolidJS provider context. These
 * primitives are part of the public SolidJS runtime package and avoid
 * unpublished shared framework subpaths.
 */

import type {
  AdapterDomain,
  AdapterStatusInfo,
  AdminAdapter,
  AiAdapter,
  AuthAdapter,
  DbAdapter,
  FileStorageAdapter,
  LoggerAdapter,
  PaymentsAdapter,
} from "@geenius/adapters";
import { createMemo } from "solid-js";
import { useAdapterContext } from "../provider";

/**
 * Reads the configured database adapter from the current SolidJS adapter context.
 *
 * @returns The active database adapter implementation for the provider tree.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
// @doc
export function createDb(): DbAdapter {
  const context = useAdapterContext();
  return context.getAdapter("db");
}

/**
 * Reads the configured authentication adapter from the current SolidJS context.
 *
 * @returns The active authentication adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
// @doc
export function createAuth(): AuthAdapter {
  const context = useAdapterContext();
  return context.getAdapter("auth");
}

/**
 * Reads the configured authentication adapter from the current SolidJS context.
 * Alias for {@link createAuth} matching the React `useAuthAdapter` convention.
 *
 * @returns The active authentication adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
// @doc
export function createAuthAdapter(): AuthAdapter {
  return createAuth();
}

/**
 * Reads the configured payments adapter from the current SolidJS context.
 *
 * @returns The active payments adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
// @doc
export function createPayments(): PaymentsAdapter {
  const context = useAdapterContext();
  return context.getAdapter("payments");
}

/**
 * Reads the configured AI adapter from the current SolidJS context.
 *
 * @returns The active AI adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
// @doc
export function createAi(): AiAdapter {
  const context = useAdapterContext();
  return context.getAdapter("ai");
}

/**
 * Reads the configured file-storage adapter from the current SolidJS context.
 *
 * @returns The active file-storage adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
// @doc
export function createFileStorage(): FileStorageAdapter {
  const context = useAdapterContext();
  return context.getAdapter("storage");
}

/**
 * Reads the configured file-storage adapter using the PRD's storage primitive name.
 *
 * @returns The active file-storage adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
// @doc
export function createStorage(): FileStorageAdapter {
  return createFileStorage();
}

/**
 * Reads the configured admin adapter from the current SolidJS context.
 *
 * @returns The active admin adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
// @doc
export function createAdmin(): AdminAdapter {
  const context = useAdapterContext();
  return context.getAdapter("admin");
}

/**
 * Reads the configured logger adapter from the current SolidJS context.
 *
 * @returns The active logger adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
// @doc
export function createLogger(): LoggerAdapter {
  const context = useAdapterContext();
  return context.getAdapter("logger");
}

/**
 * Returns the full adapter status record from the current SolidJS context.
 *
 * @returns A status object keyed by adapter domain.
 */
// @doc
export function createAdapterStatuses(): Record<
  AdapterDomain,
  AdapterStatusInfo
> {
  const context = useAdapterContext();
  return context.statuses;
}

/**
 * Creates a memoized reader for a single adapter domain status.
 *
 * @param domain Adapter domain whose status should be observed.
 * @returns A getter that always reflects the latest status for that domain.
 */
// @doc
export function createAdapterStatus(
  domain: AdapterDomain,
): () => AdapterStatusInfo {
  const context = useAdapterContext();
  return createMemo(() => context.statuses[domain]);
}

/**
 * Creates a memoized readiness reader for a single adapter domain.
 *
 * @param domain Adapter domain to inspect.
 * @returns A getter that reports whether the domain is ready for use.
 */
// @doc
export function createIsAdapterReady(domain: AdapterDomain): () => boolean {
  const context = useAdapterContext();
  return createMemo(() => context.isReady(domain));
}

/**
 * Accesses every adapter and status helper from the current provider context.
 *
 * @returns A stable object exposing adapters, statuses, and loading helpers.
 */
// @doc
export function createAdapters(): {
  db: DbAdapter | undefined;
  auth: AuthAdapter | undefined;
  payments: PaymentsAdapter | undefined;
  ai: AiAdapter | undefined;
  storage: FileStorageAdapter | undefined;
  fileStorage: FileStorageAdapter | undefined;
  admin: AdminAdapter | undefined;
  logger: LoggerAdapter | undefined;
  statuses: Record<AdapterDomain, AdapterStatusInfo>;
  isLoading: boolean;
  isReady: (domain: AdapterDomain) => boolean;
} {
  const context = useAdapterContext();

  return {
    get db() {
      return context.adapters.db;
    },
    get auth() {
      return context.adapters.auth;
    },
    get payments() {
      return context.adapters.payments;
    },
    get ai() {
      return context.adapters.ai;
    },
    get storage() {
      return context.adapters.storage;
    },
    get fileStorage() {
      return context.adapters.storage;
    },
    get admin() {
      return context.adapters.admin;
    },
    get logger() {
      return context.adapters.logger;
    },
    get statuses() {
      return context.statuses;
    },
    get isLoading() {
      return context.isLoading;
    },
    isReady: context.isReady,
  };
}
